"use strict";

/* global dataLayer */

import Mustache from "mustache";
import FractionsTools from "../fractions-tools";
import SizeSorter from "../product-size-sorter";
import Templates from "./loop54-templates";

let Settings = {};
let State = {};

let Loop54FiltersProxy = {};

class Loop54FiltersHandler {

	constructor(view, settings, state) {
		this.view = view;

		if (settings) {
			this.settings = settings;
		}

		if (state) {
			this.state = state;
		}

		Loop54FiltersProxy = this;
	}

	set settings(settings) {
		Settings = settings;
	}

	get settings() {
		return Settings;
	}

	set state(state) {
		State = state;
	}

	get state() {
		return State;
	}

	getCurrentFacets(clearFacets) {

		const possibleFacets = {};

		for (const possibleFacet of (Settings.views[this.view].possibleFacets || [])) {
			possibleFacets[possibleFacet] = [];
		}

		if (State.views[this.view].params.selectedFacets && !clearFacets) {
			for (const facet of Object.keys(State.views[this.view].params.selectedFacets)) {
				if (possibleFacets[facet]) {
					possibleFacets[facet] = State.views[this.view].params.selectedFacets[facet];
				}
			}
		} else {
			State.views[this.view].params.selectedFacets = {};
			if (State.views[this.view].elems.searchFacets) {
				const selectedFacetsBar = State.views[this.view].elems.searchFacets.querySelector(".js-selected-facets-bar");
				if (selectedFacetsBar) {
					selectedFacetsBar.classList.remove("has-facets");
					selectedFacetsBar.innerHTML = "";
				}
			}
		}

		const userFacets = Object.keys(possibleFacets).map(facet => {
			const facetObject = {
				attributeName: facet
			};

			if (possibleFacets[facet].length > 0) {
				facetObject.selected = possibleFacets[facet];
			}

			return facetObject;
		});

		State.views[this.view].params.userFacets = userFacets;

		return State.views[this.view].params.userFacets;

	}

	resetFacets() {
		if (State.views[this.view].elems.searchFacets) {

			for (const option of State.views[this.view].elems.searchFacets.querySelectorAll(".js-selected-facets-bar .facet-option.open")) {
				option.classList.remove(".open");
			}

			if (State.views[this.view].elems.searchFacets.dataset.selectedFacets !== "{}") {
				State.views[this.view].elems.searchFacets.dataset.skipFacetUpdate = true;
				State.views[this.view].params.selectedFacets = {};
				State.views[this.view].elems.searchFacets.dataset.selectedFacets = "{}";
			}

		}

		this.facetsToUrlParams();
		this.updateRobotsMetaTag();
	}

	/**
	 * Convert facets to url params. We try to sort the facets and values so that urls would always be the same
	 * regardless of in what order user selected facets.
	 */
	facetsToUrlParams() {

		if (State.firstLoad || !Settings.isCategory) {
			return;
		}

		const selectedFacets = State.views[this.view].params.selectedFacets;

		const urlParams = new URLSearchParams(window.location.search);

		const lookups = Settings.views[this.view].lookups || {};
		const facets = [];

		const idFacets = ["targetGroups", "mainColour", "brand"];
		const idParams = {
			targetGroups: new Set(),
			mainColour: new Set(),
			brand: new Set()
		};

		const urlFacets = {};

		for (const [facet, values] of Object.entries(selectedFacets)) {
			const mappedFacet = lookups[facet];

			if (idFacets.includes(facet) && (mappedFacet !== undefined)) {

				for (const value of values) {
					let mappedValue = mappedFacet[value];

					if (mappedValue) {
						idParams[facet].add(mappedFacet[value]);
					}
				}
			} else {
				urlFacets[facet] = [...values].sort();
				facets.push(`${facet}${Settings.facetsParamNameSeparator}${values.join(Settings.facetsParamValueSeparator)}`);
			}
		}

		const idParamsString = idFacets.flatMap(idParam => Array.from(idParams[idParam]).sort()).filter(Boolean).join("-");

		if (idParamsString) {
			urlParams.set(Settings.idParam, idParamsString);
		} else {
			urlParams.delete(Settings.idParam);
		}

		let rawOrderBy = String(State?.views[this.view]?.elems?.orderBy?.value);

		if (Settings.views.products.sortOrder) {
			if (rawOrderBy !== Settings.views.products.sortOrder) {
				urlParams.set(Settings.orderParam, rawOrderBy || "default");
			} else {
				urlParams.delete(Settings.orderParam);
			}
		} else {
			if (rawOrderBy) {
				urlParams.set(Settings.orderParam, rawOrderBy);
			} else {
				urlParams.delete(Settings.orderParam);
			}
		}

		if (Object.keys(urlFacets).length > 0) {
			urlParams.set(Settings.facetsParam, JSON.stringify(Object.fromEntries(Object.entries(urlFacets).sort())));
		} else {
			urlParams.delete(Settings.facetsParam);
		}

		urlParams.sort();
		const urlParamsString = urlParams.toString();

		history.replaceState(null, "", urlParamsString ? "?" + urlParamsString : window.location.pathname);

	}

	/**
	 * Convert url params to facets.
	 *
	 * @returns {Object} Facets.
	 */
	getUrlFacets() {

		if (State.firstLoad && Settings.isCategory) {
			this.updateRobotsMetaTag(Settings.views[this.view].noIndex);
		}

		const urlParams = new URLSearchParams(window.location.search);

		const facets = urlParams.get(Settings.facetsParam);

		let parsedFacets = {};

		if (facets) {
			try {
				parsedFacets = JSON.parse(facets);
			} catch (error) {
				urlParams.delete(Settings.facetsParam);
				const urlParamsString = urlParams.toString();
				history.replaceState(null, "", urlParamsString ? "?" + urlParamsString : window.location.pathname);
				return {};
			}
		}

		if (!((typeof parsedFacets === "object") && parsedFacets)) {
			return {};
		}

		return parsedFacets;

	}

	writeFacets(facets, skipFacetUpdate) {
		if (State.views[this.view].elems.searchFacets) {
			if (skipFacetUpdate) {
				State.views[this.view].elems.searchFacets.dataset.skipFacetUpdate = true;
				State.views[this.view].params.selectedFacets = facets;
			}

			State.views[this.view].elems.searchFacets.dataset.selectedFacets = JSON.stringify(State.views[this.view].params.selectedFacets);
		}
	}

	closeFilterDropdowns() {
		for (const openDropdown of document.querySelectorAll(".facet-option.open")) {
			openDropdown.classList.remove("open");
		}
		document.body.classList.remove("prevent-scroll-mobile");
		if (State.views[this.view].filterBlurListener) {
			document.body.removeEventListener("click", State.views[this.view].filterBlurListener);
			State.views[this.view].filterBlurListener = false;
		}
		// State.openFilter = false;
	}

	filterDropdownListener(remove) {
		if (remove) {
			if (State.views[this.view].filterBlurListener) {
				document.body.removeEventListener("click", State.views[this.view].filterBlurListener);
				State.views[this.view].filterBlurListener = false;
			}
		} else {
			if (!State.views[this.view].filterBlurListener) {
				State.views[this.view].filterBlurListener = (e) => {
					if (!e.target.closest(".facet-option.open, .facet-container") && !e.target.classList.contains("open-facet-options") || e.target.classList.contains("close-facet-options")) {
						Loop54FiltersProxy.closeFilterDropdowns();
					}
				};
				document.body.addEventListener("click", State.views[this.view].filterBlurListener);
			}
		}
	}

	/**
 * Update meta tag for robots if facets are applied.
 *
 * @param {boolean} [noIndexState] - Whether to set the meta tag to noindex or remove it. If not given, the meta tag will be set to noindex if facets are applied, and removed if not.
 */
	updateRobotsMetaTag(noIndexState) {

		if (!Settings.isCategory) {
			return;
		}

		let metaTag = document.querySelector('meta[name="robots"]');
		// let noIndex = Object.keys(State.views[this.view].params.selectedFacets).length > 0;
		let noIndex = false;

		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get(Settings.orderParam) || urlParams.get(Settings.idParam) || urlParams.get(Settings.facetsParam)) {
			noIndex = true;
		}

		if (typeof noIndexState === "boolean") {
			noIndex = noIndexState;
		}

		if (metaTag) {
			if (noIndex) {
				metaTag.content = "noindex";
			} else {
				metaTag.remove();
			}
		} else {
			if (noIndex) {
				const newMetaTag = document.createElement("meta");
				newMetaTag.name = "robots";
				newMetaTag.content = "noindex";
				document.head.appendChild(newMetaTag);
			}
		}
	}


	updateFacets(actionObject, skipFacetUpdate) {

		if ((actionObject.action === "removeFacet") && actionObject.clearAll) {

			State.views[this.view].params.selectedFacets = {};

		} else if ((actionObject.action === "facet") || (actionObject.action === "removeFacet" && !actionObject.clearAll)) {
			if (!State.views[this.view].params.selectedFacets[actionObject.facet]) {
				State.views[this.view].params.selectedFacets[actionObject.facet] = [];
			}

			if (actionObject.checked) {
				State.views[this.view].params.selectedFacets[actionObject.facet].push(actionObject.value);
			} else {
				State.views[this.view].params.selectedFacets[actionObject.facet] = State.views[this.view].params.selectedFacets[actionObject.facet].filter(value => !(value === actionObject.value));
			}

			// This will filter out results that do not apply.
			// For example it ["Miehet", "Naiset"] were preloaded facets, but the
			// results would only contain "Miehet", this will then remove "Naiset".
			if (Settings.isCategory && State.views[this.view].remoteFacets) {
				const remoteFacetValues = State.views[this.view].remoteFacets[actionObject.facet];

				State.views[this.view].params.selectedFacets[actionObject.facet] = State.views[this.view].params.selectedFacets[actionObject.facet].filter(selectedFacetValue => remoteFacetValues.includes(selectedFacetValue));
			}

			if (State.views[this.view].params.selectedFacets[actionObject.facet].length === 0) {
				delete State.views[this.view].params.selectedFacets[actionObject.facet];
			}

		}

		// Hack for disabling shop availability filter when sizes are selected and vice versa.
		// Filter work as colour level but these are ean level filters and we can't mix them.
		if (State.views[this.view]?.elems?.searchFacets) {
			const shopFilter = State.views[this.view].elems.searchFacets.querySelector(".facets-bar .facet-option[data-facet='sizes']");
			if (shopFilter) {
				if (State.views[this.view].params.selectedFacets.shopAvailability) {
					shopFilter.classList.add("hidden");
				} else {
					shopFilter.classList.remove("hidden");
				}
			}

			const sizesFilter = State.views[this.view].elems.searchFacets.querySelector(".facets-bar .facet-option[data-facet='shopAvailability']");
			if (sizesFilter) {
				if (State.views[this.view].params.selectedFacets.sizes) {
					sizesFilter.classList.add("hidden");
				} else {
					sizesFilter.classList.remove("hidden");
				}
			}
		}

		this.writeFacets(State.views[this.view].params.selectedFacets, skipFacetUpdate);
		this.updateRobotsMetaTag();
		this.facetsToUrlParams();

		return this.getCurrentFacets();

	}

	updateOrderBy() {
		let rawOrderBy = String(State?.views[this.view]?.elems?.orderBy?.value);
		const [orderBy, order] = rawOrderBy.split("-");
		if (orderBy) {
			State.views[this.view].params.orderBy = [{ type: "attribute", attributeName: orderBy, order: order || "asc" }];
		} else {
			State.views[this.view].params.orderBy = false;
		}

		const urlParams = new URLSearchParams(window.location.search);

		if (!State.firstLoad && Settings.isCategory) {

			if (Settings.views.products.sortOrder) {
				if (rawOrderBy !== Settings.views.products.sortOrder) {
					urlParams.set(Settings.orderParam, rawOrderBy || "default");
				} else {
					urlParams.delete(Settings.orderParam);
				}
			} else {
				if (orderBy && order) {
					urlParams.set(Settings.orderParam, rawOrderBy);
				} else {
					urlParams.delete(Settings.orderParam);
				}
			}

			urlParams.sort();
			const urlParamsString = urlParams.toString();

			history.replaceState(null, "", urlParamsString ? "?" + urlParamsString : window.location.pathname);
			this.updateRobotsMetaTag();
		}

		return State.views[this.view].params.orderBy;
	}

	handleFacets(data, reset) {
		// Currently only products, handle different views if neccessary
		if (!["products", "category"].includes(this.view)) {
			return;
		}

		if (!data) {
			return false;
		}

		if (!(data.results && data.results.facets)) {
			return false;
		}

		const facets = data.results.facets;

		State.views[this.view].remoteFacets = (facets || []).reduce((facetsLookup, facet) => {

			facetsLookup[facet.name] = facet.items.map(item => item.item);

			return facetsLookup;
		}, {});

		if (State.views[this.view].elems.searchFacets) {

			const facetsBar = State.views[this.view].elems.searchFacets.querySelector(".js-facets-bar");

			const checkboxBarFilterTemplate = Templates.checkboxBarFilter;
			Mustache.parse(checkboxBarFilterTemplate);

			const filterTemplate = Templates.filter;
			const checkboxTemplate = Templates.checkboxTemplate;
			Mustache.parse(filterTemplate);
			Mustache.parse(checkboxTemplate);

			const possibleFacets = Settings.views[this.view].possibleFacets || [];

			if (!State.views[this.view].elems.searchFacets.querySelector(".facet-option") || reset) {
				let defaultFilters = "";

				for (const facet of possibleFacets) {
					if (Settings.views[this.view].filterbarFacets.includes(facet)) {
						defaultFilters += Mustache.render(filterTemplate, { name: facet, disabled: true }, { checkboxTemplate });
					}
				}

				if (facetsBar) {
					facetsBar.innerHTML = defaultFilters;
				}
			}

			let facetLookup = {};
			if (facetsBar) {
				facetLookup = Array.from(facetsBar.querySelectorAll(".facet-option")).reduce((acc, facet) => {
					const facetName = facet.dataset.facet;
					if (facetName) {
						if (acc[facetName] === undefined) {
							acc[facetName] = {};
						}

						acc[facetName] = facet;
					}

					return acc;
				}, {});
			}

			let selectedFacets = [];

			const selectedFacetsTemplate = Templates.selectedFacets;
			Mustache.parse(selectedFacetsTemplate);

			let visibleFacets = false;
			// let checkboxBarHtml = '';

			const renderedFacets = [];

			for (const facet of facets) {

				facet.facetName = String(Settings.facetNameMapping[facet.name] || facet.name);
				if (facet.facetName.includes("|")) {
					// eslint-disable-next-line no-unused-vars
					const [additionalFieldType, additionalFieldName] = facet.facetName.split("|");
					if (additionalFieldName) {
						facet.facetName = additionalFieldName;
					}
				}


				if (facet.items.length === 1) {
					if ((facet.items[0].count >= data?.results?.count)) {
						// Don't show facets that apply to every product unless it is selected.
						if (!facet.items[0].selected) {
							continue;
						}
					}
				}

				facet.selectedCount = 0;
				facet.items = facet.items.map(item => {
					item.sort = String(item.item).toLowerCase().trim().replace(/\W/g, "") || "";
					item.id = String(String(facet.name).trim() + "-" + String(item.item).trim().replace(/\W/g, "-"));
					if (facet.name === "sizes") {
						item.sizeSort = FractionsTools.fractionToDecimal(String(item.item).toLowerCase().trim());
						item.filterName = FractionsTools.convertToHtmlFractions(item.item);
					}

					if (facet.name === "mainColour") {
						const [name, hex] = String(item.item).split("|:|");
						item.filterName = name;
						item.colorMark = hex;

						// Add border for white (Valkoinen) and colorless (Väritön)
						if (["#fbfbfb", "#ffffff"].includes(hex)) {
							item.colorWithBorder = true;
						}
					}

					if (item.selected) {
						facet.selectedCount++;
						item.facetId = facet.name;
						item.facetName = facet.facetName;
						item.checked = true;
						selectedFacets.push(item);
					}

					// For checkbox template
					let checkboxName = String(item.filterName || item.item);
					item.labelTitle = checkboxName;
					item.label = checkboxName;
					item.value = item.item;
					item.dataAttributes = [
						{
							key: "is-facet",
							value: "true"
						},
						{
							key: "value",
							value: item.item
						},
						{
							key: "facet",
							value: facet.name
						}
					];

					return item;
				});

				if (facet.selectedCount === 0) {
					facet.selectedCount = false;
				}

				if (facet.name === "sizes") {
					facet.items = SizeSorter.sortProductSizes(facet.items);
				} else if (facet.name === "targetGroups") {

					const targetGroupsOrder = Settings.targetGroupsOrder;

					facet.items = facet.items.sort((a, b) => targetGroupsOrder[a.item || 99] - targetGroupsOrder[b.item || 99]);
				} else {
					facet.items = facet.items.sort((a, b) => {
						let _a = +a.sort;
						let _b = +b.sort;
						const aIsNumber = !Number.isNaN(_a);
						const bIsNumber = !Number.isNaN(_b);
						if (aIsNumber && bIsNumber) {
							return _a - _b;
						} else {
							return a.sort.localeCompare(b.sort, "fi", { sensitivity: "base" });
						}
					});
				}

				if (facet.items.length > 0) {
					facet.onlyOptions = true;
					if (facetLookup[facet.name]) {
						renderedFacets.push(facet.name);
						if (facetLookup[facet.name] && facetLookup[facet.name].classList.contains("open")) {
							// Prevent re-rendering open dropdown.
							const dropdownButton = facetLookup[facet.name].querySelector(".open-facet-options");
							if (dropdownButton) {
								facet.buttonContentOnly = true;
								dropdownButton.innerHTML = String(Mustache.render(filterTemplate, facet, { checkboxTemplate })).trim();
							}
						} else {
							facetLookup[facet.name].innerHTML = Mustache.render(filterTemplate, facet, { checkboxTemplate });
							facetLookup[facet.name].classList.remove("disabled");
						}
						visibleFacets = true;
					}


				}

			}

			// Disable filters that didn't apply.
			for (const facet of Object.keys(facetLookup)) {
				if (!renderedFacets.includes(facet) && facetLookup[facet]) {
					facetLookup[facet].classList.add("disabled");
				}
			}

			// State.params.allFacets = JSON.parse(JSON.stringify(facets)); // Not sure we use this anywhere?

			if (State.views[this.view].elems.searchFacets) {
				/*
				const checkboxBar = State.views[this.view].elems.searchFacets.querySelector('.js-checkbox-bar');
				if (checkboxBar) {
					checkboxBar.innerHTML = checkboxBarHtml;
					if (!checkboxBarHtml) {
						checkboxBar.classList.add('no-facets');
					} else {
						checkboxBar.classList.remove('no-facets');
					}
				} */

				if (facetsBar) {
					if (visibleFacets) {
						facetsBar.classList.remove("no-facets");
					} else {
						facetsBar.classList.add("no-facets");
					}
				}

				const selectedFacetsBar = State.views[this.view].elems.searchFacets.querySelector(".js-selected-facets-bar");

				if (selectedFacetsBar) {
					if (selectedFacets.length > 0) {
						selectedFacetsBar.classList.add("has-facets");
						selectedFacetsBar.innerHTML = Mustache.render(selectedFacetsTemplate, selectedFacets);
					} else {
						selectedFacetsBar.classList.remove("has-facets");
						selectedFacetsBar.innerHTML = "";
					}
				}

			}

			if (facetsBar) {
				const openFacetOption = facetsBar.querySelector(".facet-option.open");
				if (openFacetOption) {
					facetsBar.scrollTo(openFacetOption.offsetLeft - 20, 0);
				}
			}

			if (State.views[this.view].elems.content.querySelector(".facet-option.open")) {
				document.body.classList.add("prevent-scroll-mobile");
			} else {
				document.body.classList.remove("prevent-scroll-mobile");
			}

		}

	}

}

export default Loop54FiltersHandler;
